import React, { useState, useEffect } from 'react';
import { Activity, ActivityType, activitiesAPI } from '../../services/api';
import {
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ChatBubbleLeftIcon,
  DevicePhoneMobileIcon,
  LinkIcon,
  EllipsisHorizontalIcon,
  TrashIcon,
  ClockIcon,
  UserIcon,
  TagIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Spinner, EmptyState, Badge } from '../ui';

// Simple date formatter
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

interface ActivityTimelineProps {
  clientId: number;
  onActivityCreated?: () => void;
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ clientId, onActivityCreated }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ActivityType | 'ALL'>('ALL');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadActivities();
  }, [clientId, filter, sortOrder]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const response = await activitiesAPI.getAll({
        clientId,
        type: filter !== 'ALL' ? filter : undefined,
        limit: 100,
      });

      let sortedActivities = response.data;
      if (sortOrder === 'asc') {
        sortedActivities = [...sortedActivities].reverse();
      }

      setActivities(sortedActivities);
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;

    try {
      await activitiesAPI.delete(id);
      loadActivities();
      if (onActivityCreated) onActivityCreated();
    } catch (error) {
      console.error('Failed to delete activity:', error);
      alert('Failed to delete activity');
    }
  };

  const getActivityIcon = (type: ActivityType) => {
    const iconClass = 'h-5 w-5';
    switch (type) {
      case 'CALL': return <PhoneIcon className={iconClass} />;
      case 'EMAIL': return <EnvelopeIcon className={iconClass} />;
      case 'MEETING': return <CalendarIcon className={iconClass} />;
      case 'NOTE': return <DocumentTextIcon className={iconClass} />;
      case 'TASK': return <CheckCircleIcon className={iconClass} />;
      case 'SMS': return <DevicePhoneMobileIcon className={iconClass} />;
      case 'WHATSAPP': return <ChatBubbleLeftIcon className={iconClass} />;
      case 'LINKEDIN_MESSAGE': return <LinkIcon className={iconClass} />;
      default: return <EllipsisHorizontalIcon className={iconClass} />;
    }
  };

  const getActivityColor = (type: ActivityType) => {
    switch (type) {
      case 'CALL': return 'bg-primary-100 text-primary-600';
      case 'EMAIL': return 'bg-secondary-100 text-secondary-600';
      case 'MEETING': return 'bg-success-100 text-success-600';
      case 'NOTE': return 'bg-warning-100 text-warning-600';
      case 'TASK': return 'bg-error-100 text-error-600';
      case 'SMS': return 'bg-primary-100 text-primary-600';
      case 'WHATSAPP': return 'bg-success-100 text-success-600';
      case 'LINKEDIN_MESSAGE': return 'bg-primary-100 text-primary-600';
      default: return 'bg-neutral-100 text-neutral-600';
    }
  };

  const getPriorityBadge = (priority?: string) => {
    if (!priority) return null;

    const variants: Record<string, 'neutral' | 'primary' | 'warning' | 'error'> = {
      LOW: 'neutral',
      MEDIUM: 'primary',
      HIGH: 'warning',
      URGENT: 'error',
    };

    return <Badge variant={variants[priority] || 'neutral'} size="sm">{priority}</Badge>;
  };

  const getOutcomeBadge = (outcome?: string) => {
    if (!outcome) return null;

    const successOutcomes = ['SUCCESSFUL', 'INTERESTED', 'MEETING_SCHEDULED', 'DEAL_CLOSED', 'COMPLETED'];
    const isSuccess = successOutcomes.includes(outcome);

    return <Badge variant={isSuccess ? 'success' : 'neutral'} size="sm">{outcome.replace(/_/g, ' ')}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-neutral-700">Filter:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as ActivityType | 'ALL')}
            className="rounded-lg border border-neutral-300 shadow-sm py-1.5 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-colors"
          >
            <option value="ALL">All Activities</option>
            <option value="CALL">Calls</option>
            <option value="EMAIL">Emails</option>
            <option value="MEETING">Meetings</option>
            <option value="NOTE">Notes</option>
            <option value="TASK">Tasks</option>
            <option value="SMS">SMS</option>
            <option value="WHATSAPP">WhatsApp</option>
            <option value="LINKEDIN_MESSAGE">LinkedIn</option>
          </select>
        </div>

        <button
          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
        >
          {sortOrder === 'desc' ? '↓ Newest First' : '↑ Oldest First'}
        </button>
      </div>

      {/* Timeline */}
      {activities.length === 0 ? (
        <EmptyState
          icon={<DocumentTextIcon className="h-12 w-12" />}
          title="No activities"
          description="Get started by creating a new activity."
        />
      ) : (
        <div className="flow-root">
          <ul className="-mb-8">
            {activities.map((activity, activityIdx) => (
              <li key={activity.id}>
                <div className="relative pb-8">
                  {activityIdx !== activities.length - 1 ? (
                    <span
                      className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-neutral-200"
                      aria-hidden="true"
                    />
                  ) : null}
                  <div className="relative flex items-start space-x-3">
                    <div>
                      <div className={`relative px-1 h-10 w-10 rounded-full flex items-center justify-center ring-8 ring-white ${getActivityColor(activity.type)}`}>
                        {getActivityIcon(activity.type)}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <Card variant="bordered" hoverable className="transition-shadow">
                        <CardBody className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <h4 className="text-sm font-semibold text-neutral-900">{activity.subject}</h4>
                                {activity.taskPriority && getPriorityBadge(activity.taskPriority)}
                                {activity.outcome && getOutcomeBadge(activity.outcome)}
                              </div>

                              {activity.description && (
                                <p className="text-sm text-neutral-600 mt-1">{activity.description}</p>
                              )}

                              <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-neutral-500">
                                <div className="flex items-center">
                                  <ClockIcon className="h-4 w-4 mr-1" />
                                  {formatDate(activity.createdAt)}
                                </div>

                                {activity.user && (
                                  <div className="flex items-center">
                                    <UserIcon className="h-4 w-4 mr-1" />
                                    {activity.user.firstName} {activity.user.lastName}
                                  </div>
                                )}

                                {activity.duration && (
                                  <div className="flex items-center">
                                    <ClockIcon className="h-4 w-4 mr-1" />
                                    {activity.duration} min
                                  </div>
                                )}

                                {activity.tags && activity.tags.length > 0 && (
                                  <div className="flex items-center">
                                    <TagIcon className="h-4 w-4 mr-1" />
                                    {activity.tags.join(', ')}
                                  </div>
                                )}
                              </div>

                              {/* Task-specific info */}
                              {activity.type === 'TASK' && activity.dueDate && (
                                <div className="mt-2 flex items-center text-xs">
                                  <ExclamationCircleIcon className="h-4 w-4 mr-1 text-warning-500" />
                                  <span className="text-neutral-600">
                                    Due: {new Date(activity.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                </div>
                              )}

                              {/* Meeting-specific info */}
                              {activity.type === 'MEETING' && activity.meetingStartTime && (
                                <div className="mt-2 text-xs text-neutral-600">
                                  <div>📅 {formatDate(activity.meetingStartTime)}</div>
                                  {activity.meetingLocation && <div>📍 {activity.meetingLocation}</div>}
                                  {activity.attendees && activity.attendees.length > 0 && (
                                    <div>👥 {activity.attendees.join(', ')}</div>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="flex items-center space-x-2 ml-4">
                              <button
                                onClick={() => handleDelete(activity.id)}
                                className="text-neutral-400 hover:text-error-600 transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ActivityTimeline;

